
import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Crown, AlertCircle, LogIn } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DiaryGenerator from "@/components/DiaryGenerator";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { generateDiaryEntry, saveEntry, hasReachedFreeLimit, getEntries } from "@/utils/diary";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { checkPremiumStatusSync } from "@/App";
import PremiumAlert from "@/components/PremiumAlert";
import { BabyProfileForm } from "@/components/BabyProfileForm";
import { hasBabyProfile } from "@/utils/babyProfile";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Button } from "@/components/ui/button";

const Generate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = checkPremiumStatusSync();
  const [profileSetup, setProfileSetup] = useState(false);
  const { language } = useLanguage();
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);

  useEffect(() => {
    setProfileSetup(hasBabyProfile());
  }, []);

  useEffect(() => {
    if (!user || !user.id) return;
    
    setIsCheckingLimit(true);
    hasReachedFreeLimit(user.id)
      .then((limitReached) => {
        if (limitReached && !isPremium) {
          toast.error(
            language === 'ja' 
              ? "無料エントリーの上限30件に達しました。無制限のエントリーを作成するにはプレミアムにアップグレードしてください！"
              : "You've reached the limit of 30 free entries. Upgrade to premium for unlimited entries!",
            {
              duration: 5000,
              action: {
                label: t('generate.limitReachedAction', language),
                onClick: () => navigate("/premium")
              }
            }
          );
        }
      })
      .finally(() => {
        setIsCheckingLimit(false);
      });
  }, [navigate, user, isPremium, language]);

  const handleGenerate = async (params: any) => {
    if (!user) {
      toast.error(
        language === 'ja'
          ? "エントリーを保存するにはログインが必要です"
          : "You need to sign in to save entries",
        {
          action: {
            label: language === 'ja' ? "ログイン" : "Sign In",
            onClick: () => navigate("/login", { state: { returnTo: "/generate" } })
          }
        }
      );
      return;
    }
    
    setIsCheckingLimit(true);
    
    try {
      const reachedLimit = await hasReachedFreeLimit(user.id);
      const entryIsPremium = reachedLimit && !isPremium;
      
      if (entryIsPremium && !isPremium) {
        toast.error(
          language === 'ja'
            ? "さらに多くのエントリーを作成するにはプレミアムにアップグレードする必要があります"
            : "You need to upgrade to premium to create more entries", 
          {
            action: {
              label: t('generate.limitReachedAction', language),
              onClick: () => navigate("/premium")
            }
          }
        );
        return;
      }
      
      const entry = {
        ...params,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        isPremium: entryIsPremium,
        userId: user.id
      };
      
      await saveEntry(entry);
      
      toast.success(language === 'ja' ? "日記が正常に作成されました！" : "Diary entry created successfully!");
      navigate("/entries");
    } catch (error) {
      console.error("Error generating entry:", error);
      toast.error(language === 'ja' 
        ? "エントリーの作成中にエラーが発生しました" 
        : "Error creating entry");
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleProfileComplete = () => {
    setProfileSetup(true);
  };
  
  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      <div className="container max-w-4xl mx-auto px-4">
        <div className="mb-8 animate-fade-down">
          <h1 className="text-3xl font-bold tracking-tight">
            {t('generate.title', language)}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('generate.subtitle', language)}
          </p>
          
          {!user && (
            <Alert className="mt-4">
              <LogIn className="h-4 w-4" />
              <AlertTitle>
                {language === 'ja' ? 'ゲストモード' : 'Guest Mode'}
              </AlertTitle>
              <AlertDescription>
                {language === 'ja' 
                  ? 'エントリーを保存するにはログインする必要があります。好きなだけエントリーを生成できますが、保存するにはアカウントが必要です。' 
                  : 'You need to sign in to save entries. You can generate as many entries as you like, but you need an account to save them.'}
                <Button 
                  variant="link" 
                  className="p-0 h-auto"
                  onClick={() => navigate("/login", { state: { returnTo: "/generate" } })}
                >
                  {language === 'ja' ? 'ログインする' : 'Sign in now'}
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {!isPremium && user && (
            <PremiumAlert />
          )}
          
          {isPremium && (
            <Alert className="mt-4 bg-amber-50 border-amber-200">
              <Crown className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">{t('generate.premiumActive', language)}</AlertTitle>
              <AlertDescription className="text-amber-700">
                {t('generate.premiumActiveDescription', language)}
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        {!profileSetup ? (
          <div className="my-8">
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('generate.profileSetup', language)}</AlertTitle>
              <AlertDescription>
                {t('generate.profileSetupDescription', language)}
              </AlertDescription>
            </Alert>
            <BabyProfileForm onComplete={handleProfileComplete} />
          </div>
        ) : (
          <DiaryGenerator onGenerate={handleGenerate} />
        )}
      </div>
      <Footer />
    </main>
  );
};

export default Generate;
